import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Mail, Send } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { z } from "zod";
import { useClickTracking } from "@/hooks/useClickTracking";
import { useContactContent } from "@/hooks/useContactContent";
import { useTranslation } from "react-i18next";
import { trackContactFormSubmit } from "@/lib/analytics";

interface ContactModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const ContactModal = ({ open, onOpenChange }: ContactModalProps) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { trackClick } = useClickTracking();
  const { content } = useContactContent();

  const contactSchema = z.object({
    name: z.string().trim().min(1, t('contact.nameRequired')).max(100, t('contact.nameTooLong')),
    email: z.string().trim().min(1, t('contact.emailRequired')).email(t('contact.invalidEmail')).max(255, t('contact.emailTooLong')),
    message: z.string().trim().min(10, t('contact.messageRequired')).max(1000, t('contact.messageTooLong')),
    consent: z.boolean().refine((val) => val === true, t('contact.consentRequired')),
  });
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
    consent: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open) {
      const scrollY = window.scrollY;
      document.body.style.top = `-${scrollY}px`;
      document.body.classList.add('modal-open');
      return () => {
        document.body.classList.remove('modal-open');
        document.body.style.top = '';
        window.scrollTo(0, scrollY);
      };
    }
  }, [open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validation = contactSchema.safeParse(formData);
    if (!validation.success) {
      toast({
        title: t('common.error'),
        description: validation.error.errors[0].message,
        variant: "destructive",
        duration: 5000,
      });
      return;
    }

    setIsSubmitting(true);
    trackClick('cta', 'Contact Form Submit', formData.email);

    try {
      const { error } = await supabase
        .from("contact_messages")
        .insert({
          name: validation.data.name,
          email: validation.data.email,
          message: validation.data.message,
          consent_given: validation.data.consent,
        });

      if (error) throw error;

      // Track successful contact form submission
      trackContactFormSubmit(validation.data.name, validation.data.email);

      toast({
        title: t('contact.success') + " 🎉",
        description: t('contact.successDescription'),
        duration: 5000,
      });
      
      setFormData({ name: "", email: "", message: "", consent: false });
      onOpenChange(false);
    } catch (error) {
      toast({
        title: t('contact.error'),
        description: t('contact.errorDescription'),
        variant: "destructive",
        duration: 5000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {open && createPortal(
        <div className="modal-backdrop" onClick={() => onOpenChange(false)} />,
        document.body
      )}
      <Dialog open={open} onOpenChange={onOpenChange} modal={false}>
        <DialogContent
        ref={contentRef}
        tabIndex={-1}
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          requestAnimationFrame(() => contentRef.current?.focus());
        }}
        className="sm:max-w-md p-4 sm:p-6 z-[70]"
      >
        <DialogHeader>
          <div className="inline-flex items-center justify-center w-12 h-12 sm:w-16 sm:h-16 rounded-full gradient-instagram mb-2 sm:mb-4 shadow-glow mx-auto">
            <Mail className="w-5 h-5 sm:w-8 sm:h-8 text-white" />
          </div>
          <DialogTitle className="text-xl sm:text-2xl font-bold text-center">
            {content.contact_title} <span className="gradient-text">{content.contact_title_accent}</span>
          </DialogTitle>
          <DialogDescription className="text-center text-sm sm:text-base">
            {content.contact_description}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-3 sm:gap-4 mt-3 sm:mt-4">
          <div>
            <Input
              id="name"
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder={content.contact_name_placeholder}
              className="h-10 sm:h-12 px-4 sm:px-6 border-2 focus:border-accent transition-colors"
              disabled={isSubmitting}
              autoFocus={false}
            />
          </div>

          <div>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder={content.contact_email_placeholder}
              className="h-10 sm:h-12 px-4 sm:px-6 border-2 focus:border-accent transition-colors"
              disabled={isSubmitting}
            />
          </div>

          <div>
            <Textarea
              id="message"
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              placeholder={content.contact_message_placeholder}
              className="min-h-20 sm:min-h-24 px-4 sm:px-6 py-2 sm:py-3 border-2 focus:border-accent transition-colors resize-none"
              disabled={isSubmitting}
            />
          </div>

          <div className="flex items-start gap-2">
            <Checkbox
              id="consent"
              checked={formData.consent}
              onCheckedChange={(checked) => setFormData({ ...formData, consent: checked === true })}
              disabled={isSubmitting}
              className="mt-0.5 flex-shrink-0"
            />
            <label
              htmlFor="consent"
              className="text-[11px] sm:text-xs text-muted-foreground leading-tight sm:leading-relaxed cursor-pointer"
            >
              {t('contact.consentText')}
            </label>
          </div>

          <Button
            type="submit"
            variant="gradient"
            size="lg"
            className="h-10 sm:h-12 hover:shadow-glow w-full text-sm sm:text-base"
            disabled={isSubmitting}
          >
            {isSubmitting ? t('buttons.sending') : (
              <>
                <Send className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                {content.contact_button_text}
              </>
            )}
          </Button>
        </form>
      </DialogContent>
      </Dialog>
    </>
  );
};
